import { useMemo, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CheckFilterMenu, type CheckFilterOption } from '@/components/ui/CheckFilterMenu'
import { Pagination } from '@/components/ui/Pagination'
import { Select } from '@/components/ui/Select'
import type { Incident, IncidentIndicator, IncidentStatus } from '@/lib/system-intelligence'
import { PanelEmpty, PanelError, PanelLoading } from './PanelState'

const columns = ['Date (UTC)', 'Transitions', 'Duration', 'Indicator', 'Component']

/** The contract's suggested palette, one entry per server status value. */
const dotClass: Record<IncidentStatus, string> = {
  healthy: 'bg-healthy',
  minor: 'bg-degraded',
  // Orange for major, per the contract's palette — the darker of the two ambers,
  // so minor and major stay apart at 10px.
  major: 'bg-degraded-dim',
  critical: 'bg-critical-bright',
  vendor_silent: 'bg-fg-subtle',
  maintenance: 'bg-info',
}

/** Human wording for the tooltip; the dots alone don't name what they mean. */
const statusWord: Record<IncidentStatus, string> = {
  healthy: 'Healthy',
  minor: 'Minor',
  major: 'Major',
  critical: 'Critical',
  vendor_silent: 'Vendor silent',
  maintenance: 'Maintenance',
}

/** Rows per page, and the one the table opens on. */
export const PAGE_SIZES = [10, 25, 50, 100] as const
const DEFAULT_PAGE_SIZE = 10

const indicatorClass: Record<IncidentIndicator, string> = {
  CRITICAL_FAIL: 'text-critical-bright',
  VENDOR_SILENT: 'text-fg-muted',
  DEGRADED: 'text-degraded',
  MINOR: 'text-degraded',
  LATENCY_HIGH: 'text-degraded',
  MAINTENANCE: 'text-info',
}

interface IncidentHistoryTableProps {
  incidents: Incident[]
  /** How many the window holds in total, when more were recorded than fetched. */
  total?: number
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  /** Tighter rows, for a page tuned to fit the viewport. */
  compact?: boolean
}

/**
 * Every status transition the feed recorded for this system over the trailing
 * 30 days, newest first.
 *
 * One row per transition with the indicator code the vendor sent. The codes are
 * left raw (CRITICAL_FAIL, not "Failed") because they are what appears in the
 * audit log and the vendor's own console — translating them here would make the
 * two impossible to line up.
 *
 * There is no expandable detail any more, and Component is a dash: the contract
 * is explicit that per-incident root cause and component are not available yet
 * (§5). An empty drawer under every row would have promised a detail view that
 * does not exist, so the rows stay flat until the feed carries one.
 *
 * Filtering and paging both happen here, over the rows already fetched, rather
 * than on the server. `{svc}/incidents` does support `limit`/`offset`, but it
 * has no indicator parameter — so paging server-side would leave the filter
 * narrowing only the visible page and reporting counts for it, which is a wrong
 * answer rather than an awkward one. The caller fetches the whole window in one
 * request instead (`INCIDENT_FETCH_LIMIT`), which at current volumes is a
 * couple of dozen rows.
 *
 * `total` is what guards that trade: when the window holds more than was
 * fetched, the footer says so, and that is the signal to move paging server-side
 * and give the filter a real query parameter.
 */
export function IncidentHistoryTable({
  incidents,
  total,
  isLoading,
  error,
  onRetry,
  compact,
}: IncidentHistoryTableProps) {
  /** Chosen indicator codes; empty means every indicator (see `CheckFilterMenu`). */
  const [indicators, setIndicators] = useState<string[]>([])
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)

  /** Row padding — the deepest lever on a page tuned to fit the viewport. */
  const cell = compact ? 'px-5 py-2.5' : 'px-5 py-4'

  /**
   * The menu offers only the indicators actually present, each with its row
   * count — a static list of all six would advertise MAINTENANCE on a service
   * that has never had a maintenance window, and ticking it would blank the
   * table for no discoverable reason.
   */
  const options = useMemo<CheckFilterOption[]>(() => {
    const counts = new Map<string, number>()
    for (const incident of incidents) {
      counts.set(incident.indicator, (counts.get(incident.indicator) ?? 0) + 1)
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([value, count]) => ({
        value,
        label: value,
        count,
        // The option wears the same colour the column does, so the menu and the
        // rows it filters read as one thing.
        className: indicatorClass[value as IncidentIndicator],
      }))
  }, [incidents])

  const visible = useMemo(
    () =>
      indicators.length === 0
        ? incidents
        : incidents.filter((i) => indicators.includes(i.indicator)),
    [incidents, indicators],
  )

  /**
   * The page actually rendered.
   *
   * Clamped rather than trusted: narrowing the filter while on page 4 would
   * otherwise leave the table blank on a page that no longer exists. The
   * handlers reset to page 1 as well — this is the safety net for a `visible`
   * that shrinks underneath us on a poll.
   */
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const start = (safePage - 1) * pageSize
  const rows = visible.slice(start, start + pageSize)

  /** The window holds more than one request could carry — see the class comment. */
  const truncatedWindow = typeof total === 'number' && total > incidents.length

  /**
   * The footer is only worth its height when there is something to page. Below
   * the smallest page size every row is already on screen and the controls would
   * do nothing — which matters on the compact pages, where the panel is sized to
   * fit the viewport without scrolling.
   */
  const showFooter = visible.length > Math.min(...PAGE_SIZES) || truncatedWindow

  /** Ticking every indicator is still "unfiltered", so it must not say "8 of 8". */
  const filterLine =
    visible.length < incidents.length ? `${visible.length} of ${incidents.length} match` : null

  return (
    <section className="border-line bg-surface rounded-lg border border-dashed">
      <div
        className={cn(
          'border-line flex items-center justify-between gap-3 border-b px-5',
          compact ? 'py-3' : 'py-4',
        )}
      >
        <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          Downtime Incident History
        </h2>
        <div className="flex items-center gap-3">
          {filterLine && (
            <span className="text-fg-subtle font-mono text-[11px] tracking-[0.06em] uppercase">
              {filterLine}
            </span>
          )}
          {/* Hidden while there is nothing to filter — a control that can only
              ever return the same rows is noise. */}
          {options.length > 1 && (
            <CheckFilterMenu
              options={options}
              selected={indicators}
              onChange={(next) => {
                setIndicators(next)
                // A narrower set means fewer pages; staying on page 4 of 2 would
                // land the reader on nothing.
                setPage(1)
              }}
              label="Filter incidents by indicator"
              title="Indicator"
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <PanelLoading />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} />
      ) : incidents.length === 0 ? (
        <PanelEmpty label="No status changes recorded in the last 30 days." />
      ) : visible.length === 0 ? (
        <PanelEmpty label="No incidents match the selected indicators." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-line border-b">
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={cn(
                        'text-fg-subtle text-left font-mono text-[11px] font-semibold',
                        'tracking-[0.08em] uppercase',
                        compact ? 'px-5 py-2' : 'px-5 py-3',
                      )}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((incident) => (
                  <tr
                    key={incident.id}
                    className="border-line hover:bg-surface-2 border-b transition-colors"
                  >
                    <td
                      className={cn(cell, 'text-fg-muted font-mono text-[13px] whitespace-nowrap')}
                    >
                      {incident.date}
                    </td>
                    <td className={cell}>
                      <span
                        className="flex items-center gap-2"
                        title={`${statusWord[incident.from] ?? incident.from} → ${statusWord[incident.to] ?? incident.to}`}
                      >
                        <span className={cn('size-2.5 rounded-full', dotClass[incident.from])} />
                        <ArrowRight className="text-fg-subtle size-3.5" />
                        <span className={cn('size-2.5 rounded-full', dotClass[incident.to])} />
                      </span>
                    </td>
                    <td
                      className={cn(
                        cell,
                        'font-mono text-[13px] whitespace-nowrap',
                        // An open incident is the row worth noticing, and "Ongoing"
                        // is not a duration — colour says which it is.
                        incident.ongoing ? 'text-critical-bright font-semibold' : 'text-fg',
                      )}
                    >
                      {incident.duration}
                    </td>
                    <td
                      className={cn(
                        cell,
                        'font-mono text-[13px] whitespace-nowrap',
                        indicatorClass[incident.indicator] ?? 'text-fg',
                      )}
                    >
                      {incident.indicator}
                    </td>
                    {/* Not carried by the feed yet — a dash, not a blank cell. */}
                    <td
                      className={cn(cell, 'text-fg-subtle')}
                      title="Per-incident component is not reported by the feed yet"
                    >
                      —
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showFooter && (
            <div
              className={cn(
                'border-line flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t px-5',
                compact ? 'py-2.5' : 'py-3',
              )}
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <label className="text-fg-subtle flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] uppercase">
                  Rows
                  <Select
                    value={String(pageSize)}
                    onChange={(next) => {
                      setPageSize(Number(next))
                      // Row 11 is on page 2 at ten a page and page 1 at fifty;
                      // returning to the top is the only answer that is right
                      // for every size.
                      setPage(1)
                    }}
                    aria-label="Rows per page"
                    className="h-8 w-auto pr-9 pl-2.5 font-mono text-[11px]"
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </Select>
                </label>

                <span className="text-fg-subtle font-mono text-[11px] tracking-[0.06em] uppercase">
                  {visible.length === 0
                    ? 'No incidents'
                    : `Showing ${start + 1}–${start + rows.length} of ${visible.length}`}
                </span>

                {/* The window held more than one request can carry, so "of N"
                    above is the fetched set rather than the whole 30 days. Saying
                    so is what stops the count being read as the full picture. */}
                {truncatedWindow && (
                  <span
                    title={`The last 30 days hold ${total} status changes; the newest ${incidents.length} were fetched.`}
                    className="border-degraded/50 text-degraded cursor-help rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-[0.06em] uppercase"
                  >
                    Newest {incidents.length} of {total}
                  </span>
                )}
              </div>

              <Pagination
                page={safePage}
                pageSize={pageSize}
                total={visible.length}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </section>
  )
}
